import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Trash2, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CareTeamEditor = ({ careTeam = [], onChange }) => {
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [showExternalForm, setShowExternalForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAvailableUsers();
  }, []);

  const fetchAvailableUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/providers`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAvailableUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load available users');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get user's display name
  const getUserDisplayName = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.full_name || 'Unknown User';
  };

  const addSystemUser = (user) => {
    // Check if user is already in care team
    const exists = careTeam.some(member => member.user_id === user.id);
    if (exists) {
      toast.error('This user is already in the care team');
      return;
    }

    const displayName = getUserDisplayName(user);
    const newMember = {
      id: `care_${Date.now()}`,
      name: displayName,
      role: user.provider_role || user.role,
      clinic_hours: user.clinic_hours || '',
      phone: user.phone || '',
      email: user.email,
      user_type: user.role === 'provider' ? 'provider' : 'admin',
      user_id: user.id
    };

    onChange([...careTeam, newMember]);
    setShowUserSelector(false);
    setSearchQuery('');
    toast.success(`Added ${displayName} to care team`);
  };

  const addExternalMember = () => {
    const newMember = {
      id: `care_${Date.now()}`,
      name: '',
      role: '',
      clinic_hours: '',
      phone: '',
      email: '',
      user_type: 'external',
      user_id: null
    };

    onChange([...careTeam, newMember]);
    setShowExternalForm(false);
  };

  const updateMember = (index, field, value) => {
    const updated = [...careTeam];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeMember = (index) => {
    const updated = careTeam.filter((_, i) => i !== index);
    onChange(updated);
  };

  const filteredUsers = availableUsers.filter(user => {
    const displayName = getUserDisplayName(user).toLowerCase();
    const searchLower = searchQuery.toLowerCase();
    return displayName.includes(searchLower) || user.email.toLowerCase().includes(searchLower);
  });

  return (
    <div className="space-y-4">
      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          type="button"
          onClick={() => {
            setShowUserSelector(!showUserSelector);
            setShowExternalForm(false);
          }}
          className="btn-secondary text-sm"
        >
          <UserPlus size={16} className="mr-1" />
          Add System User
        </Button>
        <Button
          type="button"
          onClick={() => {
            setShowExternalForm(!showExternalForm);
            setShowUserSelector(false);
          }}
          className="btn-ghost text-sm"
        >
          <Users size={16} className="mr-1" />
          Add External Member
        </Button>
      </div>

      {/* System User Selector */}
      {showUserSelector && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-slate-900 mb-3">Select a Provider or Admin</h3>
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="mb-3"
          />
          <div className="max-h-60 overflow-y-auto space-y-2">
            {loading ? (
              <p className="text-sm text-slate-600">Loading users...</p>
            ) : filteredUsers.length === 0 ? (
              <p className="text-sm text-slate-600">No users found</p>
            ) : (
              filteredUsers.map(user => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 bg-white rounded border border-slate-200 hover:border-cyan-400 cursor-pointer"
                  onClick={() => addSystemUser(user)}
                >
                  <div>
                    <p className="font-medium text-slate-900">{getUserDisplayName(user)}</p>
                    <p className="text-xs text-slate-600">{user.email}</p>
                    {user.provider_role && (
                      <p className="text-xs text-cyan-700">{user.provider_role}</p>
                    )}
                  </div>
                  <div className="px-2 py-1 bg-cyan-100 text-cyan-700 text-xs rounded">
                    {user.role}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* External Member Form */}
      {showExternalForm && (
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-slate-900">External care team members are non-system users</h3>
            <Button
              type="button"
              onClick={addExternalMember}
              className="btn-primary text-sm"
            >
              Add External Member
            </Button>
          </div>
          <p className="text-xs text-slate-600">
            Click "Add External Member" to add a new form for external team member details.
          </p>
        </div>
      )}

      {/* Care Team Members List */}
      <div className="space-y-3">
        {careTeam.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No care team members assigned yet</p>
        ) : (
          careTeam.map((member, index) => (
            <div
              key={member.id}
              className="p-4 bg-slate-50 rounded-lg border border-slate-200"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-900">
                    {member.user_type === 'external' ? 'External Member' : 'System User'}
                  </h3>
                  {member.user_type !== 'external' && (
                    <div className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs rounded">
                      {member.user_type}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={() => removeMember(index)}
                  className="btn-ghost text-red-600 text-xs"
                >
                  <Trash2 size={14} />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={member.name}
                    onChange={(e) => updateMember(index, 'name', e.target.value)}
                    className="mt-1"
                    disabled={member.user_type !== 'external'}
                  />
                </div>
                <div>
                  <Label className="text-xs">Role</Label>
                  <Input
                    value={member.role}
                    onChange={(e) => updateMember(index, 'role', e.target.value)}
                    className="mt-1"
                    disabled={member.user_type !== 'external'}
                  />
                </div>
                <div>
                  <Label className="text-xs">Clinic Hours</Label>
                  <Input
                    value={member.clinic_hours}
                    onChange={(e) => updateMember(index, 'clinic_hours', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={member.phone || ''}
                    onChange={(e) => updateMember(index, 'phone', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={member.email || ''}
                    onChange={(e) => updateMember(index, 'email', e.target.value)}
                    className="mt-1"
                    disabled={member.user_type !== 'external'}
                  />
                </div>
              </div>

              {member.user_type !== 'external' && (
                <p className="text-xs text-slate-500 mt-2">
                  System user fields (name, role, email) are read-only. Only clinic hours and phone can be edited.
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CareTeamEditor;
